import { api } from 'api';
import SearchIcon from 'assets/ver2/icons/search-icon-2.svg';
import { preventKeyDownEnter } from 'components/common/func';
import { ADDRESS_TRIM_REGEX } from 'constants';
import { DEBOUNCE_SEC, ENTER_KEY_DOWN } from 'constants/common';
import { PII_PROFILE_RESIDENTIAL_ADDRESS_FINDER_NAME } from 'features/pii/const';
import { addressFinderTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useFormInput from 'hooks/form/useFormInput';
import useCommonStore from 'hooks/useCommonStore';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { cursorPointerMixin, paragraph500Mixin } from 'styles';

const FormInputAddressFinderValidation = ({
  field,
  information,
  setInformation,
  validation,
  setValidation,
  addressSuggestion,
  setAddressSuggestion,
}) => {
  const { t } = useLang();
  const { isLoading, setLoading } = useCommonStore();

  const {
    inputRef,
    resetFormInput,
    setInformationFormInput,
    getValueTrimAll,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const searchValue = information[PII_PROFILE_RESIDENTIAL_ADDRESS_FINDER_NAME];

  // Last typed text that failed addressFinderTextValid. The box is only flagged while
  // it still holds exactly that text, so a selected or prefilled address is
  // never validated.
  const [invalidText, setInvalidText] = useState(null);
  const invalidTextRef = useRef(null);

  const isTypedTextInvalid = () =>
    invalidTextRef.current !== null &&
    invalidTextRef.current === inputRef.current?.value;
  const isSearchInvalid =
    invalidText !== null && invalidText === (information[field.name] || '');

  const updateInvalidText = (value) => {
    invalidTextRef.current = value;
    setInvalidText(value);
  };

  // A queued or in-flight lookup is dropped when its typed text is invalid or
  // the box has since been cleared (the debounce has no cancel).
  const shouldDropLookup = () =>
    isTypedTextInvalid() || !(inputRef.current?.value ?? '').trim();

  const fetchLookupAddressSuggestion = async (value) => {
    try {
      const { data } = await api.lookupAddresses(
        value,
        'd6fe8ff05352dfff3c3a3367d7140364'
      );
      console.log(
        `⭐ FormInputAddressFinderValidation 👉 fetchLookupAddressSuggestion ➡️ data ↩`,
        data
      );
      if (shouldDropLookup()) return;
      if (data?.payload) {
        setAddressSuggestion(data.payload);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 FormInputAddressFinderValidation ❌ fetchLookupAddressSuggestion ➡️ ERROR ↩`,
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };
  const checkLookupAddressSuggestion = async (value) => {
    if (!value) return;
    if (shouldDropLookup()) return;
    try {
      fetchLookupAddressSuggestion(value);
    } catch (error) {
      console.error(
        `🔥 FormInputAddressFinderValidation ❌ checkLookupAddressSuggestion ➡️ ERROR ↩`,
        error?.message
      );
    }
  };
  const { debounce: checkLookupAddressSuggestionDebounce } =
    useDebounceCallback((value) => {
      checkLookupAddressSuggestion(value);
    }, DEBOUNCE_SEC);

  const onReset = () => {
    resetFormInput({
      information,
      setInformation,
      validation,
      setValidation,
    });
    setAddressSuggestion(null);
  };

  const handleOnClickSearchIconButton = () => {
    if (isEmpty(searchValue)) return;
    if (isLoading) return;
    if (!isEmpty(addressSuggestion)) return;
    setLoading(true);
    checkLookupAddressSuggestion(searchValue);
  };

  const handleOnChangeFormInputAddressFinder = (e) => {
    const { value } = e.target;

    if (!value) {
      updateInvalidText(null);
      onReset();
      return;
    }
    setInformationFormInput({
      value,
      information,
      setInformation,
    });

    if (!addressFinderTextValid(value)) {
      updateInvalidText(value);
      setAddressSuggestion(null);
      return;
    }
    updateInvalidText(null);

    checkLookupAddressSuggestionDebounce(value);
  };
  const handleOnBlurFormInputAddressFinder = (e) => {
    const { value } = e.target;

    if (!value) return;
    const newValue = getValueTrimAll(value, true, ADDRESS_TRIM_REGEX);
    if (isTypedTextInvalid()) {
      updateInvalidText(addressFinderTextValid(newValue) ? null : newValue);
    }
    setInformationFormInput({
      value: newValue,
      information,
      setInformation,
    });
  };
  const handleOnKeyDownFormInputAddressFinder = (e) => {
    if (isEmpty(searchValue)) return;
    const { key } = e;
    if (key === ENTER_KEY_DOWN) {
      handleOnClickSearchIconButton();
    }
  };

  return (
    <FormGroupStyled onKeyDown={preventKeyDownEnter}>
      <FormLabel styles={{ marginBottom: '4px' }} />
      <FormControl
        ref={inputRef}
        type={field.type}
        name={field.name}
        value={information[field.name] || ''}
        defaultValue={information[field.name]}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        isInvalid={validation[field.name] || isSearchInvalid}
        onChange={handleOnChangeFormInputAddressFinder}
        onBlur={handleOnBlurFormInputAddressFinder}
        // onKeyDown={handleOnKeyDownFormInputAddressFinder}
      />
      <SearchIconButton
        src={SearchIcon}
        height={20}
        width={20}
        // onClick={handleOnClickSearchIconButton}
      />
      <FormControlFeedback
        value={
          isSearchInvalid
            ? t('form_msg_search_validation')
            : t(validation[field.name])
        }
        validationStyles={{ position: 'unset' }}
      />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  position: relative;
  ${Fade};
`;

const FormControl = styled(Form.Control)`
  ${paragraph500Mixin};
  text-transform: uppercase;
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 8px;
  padding-inline: 42px 10px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  &::placeholder {
    ${paragraph500Mixin};
    text-transform: uppercase;
    color: var(--ds-c-blue-disabled) !important;
  }
`;

const SearchIconButton = styled.img`
  ${cursorPointerMixin};
  position: absolute;
  top: 36px;
  left: 12px;
`;

export default FormInputAddressFinderValidation;
